import { useState, useRef } from 'react';
import flow from 'lodash/fp/flow';
import cond from 'lodash/fp/cond';
import get from 'lodash/fp/get';
import negate from 'lodash/fp/negate';
import noop from 'lodash/fp/noop';
import { parseISO, isValid, format, addMonths } from 'date-fns/fp';
import useOutsideClick from '../../../../hooks/useOutsideClick';
import FocusLock from 'react-focus-lock';

import TextField from '../TextField';
import Calendar from '../../../CalendarOld';
import './DatePicker.scss';

export default function DatePicker({
	id,
	name,
	value,
	displayFormat = 'MMM d, yyyy',
	min,
	max,
	onChange = noop,
	onClick = noop,
	onBlur = noop,
	onKeyUp = noop,
	label,
	...props
}) {
	const [showDatePicker, setShowDatePicker] = useState(false);
	const openDatePicker = () => setShowDatePicker(true);
	const closeDatePicker = () => setShowDatePicker(false);

	const outsideClickRef = useRef(null);

	const handleChange = flow(
		get('start'),
		format('yyyy-MM-dd'),
		nextDate => onChange({ target: { id, name, value: nextDate } }),
		closeDatePicker
	);

	const handleClick = evt => {
		openDatePicker();
		onClick(evt);
	};

	const handleKeyDown = evt => {
		if (evt.key === 'Enter' || evt.key === ' ') {
			evt.preventDefault();
			openDatePicker();
		}
	};

	const handleKeyUp = evt => {
		if (evt.key === 'ArrowDown') {
			openDatePicker();
			onKeyUp(evt);
		}
	};

	const handleBlur = evt => {
		onBlur(evt);
	};

	useOutsideClick(outsideClickRef, closeDatePicker);

	const valueAsDate = parseISO(value);

	const getDisplayValue = cond([
		[isValid, format(displayFormat)],
		[negate(isValid), () => ''],
	]);

	const availableRange = {
		start: min ? parseISO(min) : new Date(),
		end: max ? parseISO(max) : addMonths(13)(new Date()),
	};

	const calendarId = `${id || name}-calendar`;

	return (
		<div className="DatePicker">
			<TextField
				id={id}
				name={name}
				value={getDisplayValue(valueAsDate)}
				onClick={handleClick}
				onBlur={handleBlur}
				onKeyDown={handleKeyDown}
				onKeyUp={handleKeyUp}
				label={label}
				readOnly
				aria-haspopup="dialog"
				aria-expanded={showDatePicker}
				aria-controls={calendarId}
			/>

			{showDatePicker && (
				<div
					className="DatePicker__calendar-container"
					ref={outsideClickRef}
					id={calendarId}
					role="dialog"
					aria-modal="true"
					aria-label={label}
				>
					<FocusLock returnFocus>
						<Calendar
							isDesktop
							isRange={false}
							selectedRange={{ start: valueAsDate, end: valueAsDate }}
							availableRange={availableRange}
							onRangeChange={handleChange}
							onClose={closeDatePicker}
						/>
					</FocusLock>
				</div>
			)}
		</div>
	);
}
